import { RoomProvider } from "@/context/RoomContext";
import { useShowDebugItems } from "@/hooks/useShowDebugItems";
import { RoomMeasurementKey, getLineItemState, isItemArchived, makeRoomItemComparator, type LineItemState } from "@rebuild/shared";
import { getKeyForMultiSelect } from "@/utils/utils";
import type { MappedTuple, PageMetadata } from "@/interfaces/estimate-detail";
import type { EstimateDetail } from "@/interfaces/estimate-detail";
import type { MeasurementData } from "@/interfaces/estimation.interface";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useCopyPaste } from "./hooks/useCopyPaste";
import { usePasteLineItemsMutation } from "./hooks/usePasteLineItemsMutation";
import { Box } from "lucide-react";
import AddLineItem from "./AddLineItem";
import type { Category } from "./Lines";
import { Lines } from "./Lines";
import type { MetricProps } from "./RoomHeader/Metric";
import { RoomHeader } from "./RoomHeader/RoomHeader";
import { reorderLines } from "./util/reorder-lines";
import { store, useDispatch, useSelector } from "@/redux/store";
import { estimationActions } from "@/redux/actions/estimation.actions";
import { estimationSelector } from "@/redux/selectors/estimation.selector";
import type { SelectedLineItem } from "@/redux/slices/estimation.slice";
import { getLineItemComponentKey } from "@/util/utils";
import estimateProvider from "@/providers/estimate.provider";
import { useAnalytics } from "@/analytics";
import { toast } from "sonner";

interface RoomProps {
  estimateId: string;
  room: EstimateDetail;
  onSelectItemLine: (uniqueId: string, category?: Category) => void;
  onItemLineDelete: (itemId: string, pageId: string, uniqueId: string, category?: Category) => void;
  updateMeasurement: (measurementData: MeasurementData[]) => void;
  updateMetadataForExpectedDoc: (metadataUpdates: Partial<PageMetadata>) => void;
  updateLineItems: (pageId: string, lineItems: MappedTuple[]) => Promise<boolean>;
}

export const Room = memo(function Room({
  estimateId,
  room,
  onItemLineDelete,
  onSelectItemLine,
  updateMeasurement,
  updateMetadataForExpectedDoc,
  updateLineItems,
}: RoomProps) {
  const [isMouseHoveringOver, setIsMouseHoveringOver] = useState(false);

  const pageId = room.dataForUI.metadata.pageId;
  const lines = room.dataForUI.page.tuplesOutput;

  // Read estimation directly; this selector doesn't fire on estimationDetail changes, so it
  // doesn't break the memoization we get from stable props.
  const estimation = useSelector(estimationSelector.estimation);

  const dispatch = useDispatch();
  const isDebugItemsEnabled = useShowDebugItems();
  const selectedLineItems = useSelector(estimationSelector.selectedLineItems);
  const selectedCount = selectedLineItems.length;
  const isSelected = (item: SelectedLineItem) => selectedLineItems.some((s) => getKeyForMultiSelect(s) === getKeyForMultiSelect(item));
  const toggleSelection = (item: SelectedLineItem) => dispatch(estimationActions.toggleLineItemSelection(item, true));
  const clearSelection = () => dispatch(estimationActions.clearLineItemSelection());
  const { copiedLineItems } = useCopyPaste(estimateId);
  const pasteLineItemsMutation = usePasteLineItemsMutation();

  const handlePaste = useCallback(async () => {
    if (copiedLineItems.length === 0) return;
    await pasteLineItemsMutation.mutateAsync({
      selectedItems: copiedLineItems,
      estimateId,
      destinationPageId: pageId,
      cutOperation: false,
    });
    // Silently refetch estimate detail and update Redux without triggering the loading skeleton
    try {
      const response = await estimateProvider.getEstimateDetail(estimateId);
      if (response.success) {
        dispatch(
          estimationActions.setEstimationState({
            estimationDetail: response.data,
            files: response.files ?? [],
          }),
        );
      }
    } catch {
      // Silent failure — data will be stale until next navigation
    }
  }, [copiedLineItems, estimateId, pageId, pasteLineItemsMutation, dispatch]);

  const metrics = estimation?.measurementData?.find((measurement) => measurement.id === pageId)?.measurement;

  const onUpdateMeasurement = useCallback(
    (category: RoomMeasurementKey, value: string) => {
      const updatedMeasurementData = [...(estimation?.measurementData ?? [])];
      const existingIndex = updatedMeasurementData.findIndex((data) => data.id === pageId);

      if (existingIndex !== -1) {
        updatedMeasurementData[existingIndex] = {
          ...updatedMeasurementData[existingIndex],
          measurement: {
            ...updatedMeasurementData[existingIndex].measurement,
            [category]: +value,
          },
        };
      } else {
        updatedMeasurementData.push({
          id: pageId,
          measurement: { ...metrics, [category]: +value },
        });
      }

      updateMeasurement([...updatedMeasurementData]);
    },
    [estimation?.measurementData, metrics, pageId, updateMeasurement],
  );

  const roomMetrics: MetricProps[] = [
    {
      value: metrics?.[RoomMeasurementKey.Walls] || 0,
      type: "sf",
      icon: <Box className="h-4 w-4 text-[#71717A]" />,
      label: "Walls",
      category: RoomMeasurementKey.Walls,
    },
    {
      value: metrics?.[RoomMeasurementKey.Floors] || 0,
      type: "sf",
      icon: <img src="/rectangle-floor.svg" />,
      label: "Floors",
      category: RoomMeasurementKey.Floors,
    },
    {
      value: metrics?.[RoomMeasurementKey.Ceilings] || 0,
      type: "sf",
      icon: <img src="/rectangle-roof.svg" />,
      label: "Ceilings",
      category: RoomMeasurementKey.Ceilings,
    },
    {
      value: metrics?.[RoomMeasurementKey.FloorPerimeter] || 0,
      type: "lf",
      icon: <img src="/rectangle-floor-area.svg" />,
      label: "Perimeter of Floor",
      category: RoomMeasurementKey.FloorPerimeter,
    },
    {
      value: metrics?.[RoomMeasurementKey.CeilingPerimeter] || 0,
      type: "lf",
      icon: <img src="/rectangle-roof-area.svg" />,
      label: "Perimeter of Ceiling",
      category: RoomMeasurementKey.CeilingPerimeter,
    },
  ];

  const sort = makeRoomItemComparator(lines);

  const mappedTuplesNotDeleted: MappedTuple[] = useMemo(
    () =>
      lines
        .flatMap((tuple) =>
          tuple.lineItems.map((lineItem) => ({
            tuple,
            currentItem: lineItem,
            // we use this as a key when rendering line components, we want to re-render the component when and only when any
            // of the fields in the line item change
            uuid: getLineItemComponentKey(pageId, tuple, lineItem),
          })),
        )
        .filter(({ tuple, currentItem }) => isDebugItemsEnabled || (!isItemArchived(tuple) && !isItemArchived(currentItem))),
    [lines, isDebugItemsEnabled, pageId],
  );

  const byState = (...states: LineItemState[]) =>
    mappedTuplesNotDeleted.filter(({ tuple, currentItem }) => states.includes(getLineItemState(tuple, currentItem, lines))).sort(sort);

  const itemCategoryTab: Record<Category, MappedTuple[]> = {
    issues: byState("issue"),
    recommended: byState("rec"),
    removeRules: byState("excluded"),
  };

  const _linesFiltered = useMemo(() => byState("line", "comp"), [mappedTuplesNotDeleted]);

  const [linesFiltered, setLinesFiltered] = useState<MappedTuple[]>([]);
  useEffect(() => setLinesFiltered(_linesFiltered), [_linesFiltered]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Shift+ArrowDown
      if (event.shiftKey && event.key === "ArrowDown") {
        // Read lineItemDetail from the store on demand so Room doesn't subscribe to it — that
        // subscription fires on every line item edit and would defeat React.memo.
        const lineItemDetail = store.getState().estimation.lineItemDetail;
        if (!isMouseHoveringOver || lineItemDetail === undefined) {
          return;
        }

        // step 1: if user is holding shift+ArrowDown, auto-select current line item
        const lineItemDetailItem = {
          tupleId: lineItemDetail.tuple.tupleId,
          lineItemId: lineItemDetail.lineItem.lineItemId,
          pageId,
          lineItem: lineItemDetail.lineItem.lineItem,
        } as SelectedLineItem;

        if (!isSelected(lineItemDetailItem)) {
          toggleSelection(lineItemDetailItem);
        }

        // step 2: find last line item that's selected
        let lastSelectedLineIndex = -1;
        for (let i = linesFiltered.length - 1; i >= 0; i -= 1) {
          const line = linesFiltered[i];
          const item = {
            tupleId: line.tuple.tupleId,
            lineItemId: line.currentItem.lineItemId,
            pageId: pageId,
            lineItem: line.currentItem.lineItem,
          } as SelectedLineItem;

          if (isSelected(item)) {
            lastSelectedLineIndex = i;
            break;
          }
        }

        if (lastSelectedLineIndex === -1) {
          return;
        }

        // step 3: select the next line item after the latest selected one
        const currentLine = linesFiltered[lastSelectedLineIndex + 1];
        const selectedLineItem = {
          tupleId: currentLine.tuple.tupleId,
          lineItemId: currentLine.currentItem.lineItemId,
          pageId,
          lineItem: currentLine.currentItem.lineItem,
        };
        toggleSelection(selectedLineItem);
      }
      if (event.key === "Escape") {
        clearSelection();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [room, selectedCount, isMouseHoveringOver]);

  const { trackEvent, ANALYTICS_EVENTS } = useAnalytics();

  const handleReorder = useCallback(
    async (activeUuid: string, overUuid: string, draggedUuids?: Array<string>) => {
      const movingUuids = draggedUuids ?? [activeUuid];
      const reordered = reorderLines(linesFiltered, activeUuid, overUuid, movingUuids);
      if (!reordered) return;

      const fromIndex = linesFiltered.findIndex((l) => l.uuid === activeUuid);
      const toIndex = linesFiltered.findIndex((l) => l.uuid === overUuid);
      const movedIndices = movingUuids.map((uuid) => linesFiltered.findIndex((l) => l.uuid === uuid)).filter((idx) => idx !== -1);
      const insertIndex = reordered.findIndex((l) => movingUuids.includes(l.uuid));
      const isContiguousSelection = Math.max(...movedIndices) - Math.min(...movedIndices) + 1 === movedIndices.length;

      const withNewOrder: MappedTuple[] = reordered.map((line, idx) => ({
        ...line,
        currentItem: {
          ...line.currentItem,
          orderIndex: idx,
          movedByUser: true,
        },
      }));

      const previousOrder = linesFiltered;
      setLinesFiltered(withNewOrder);
      const success = await updateLineItems(pageId, withNewOrder);
      if (success) {
        trackEvent(ANALYTICS_EVENTS.LINE_ITEM_REORDERED, {
          estimate_id: estimateId,
          page_id: pageId,
          line_item_id: linesFiltered[fromIndex].currentItem.lineItemId,
          from_index: fromIndex,
          to_index: toIndex,
          item_count: movingUuids.length,
          insert_index: insertIndex,
          is_contiguous_selection: isContiguousSelection,
        });
      } else {
        setLinesFiltered(previousOrder);
        toast.error("Failed to reorder line items. Please try again.");
      }
    },
    [linesFiltered, updateLineItems, pageId, trackEvent, ANALYTICS_EVENTS, estimateId],
  );

  return (
    <div className="flex flex-col" onMouseEnter={() => setIsMouseHoveringOver(true)} onMouseLeave={() => setIsMouseHoveringOver(false)}>
      <RoomHeader
        estimateId={estimateId}
        room={room}
        metrics={roomMetrics}
        onUpdateMeasurement={onUpdateMeasurement}
        onUpdateRoomName={(newRoomName) => updateMetadataForExpectedDoc({ roomName: newRoomName, pageId })}
        copiedItemCount={copiedLineItems.length}
        onPaste={handlePaste}
        isPasting={pasteLineItemsMutation.isPending}
      />

      <RoomProvider
        filterLines={linesFiltered || []}
        itemCategoryTab={itemCategoryTab}
        deleteLineItem={async (tupleId: string, uniqueId: string, category?: Category) => {
          await onItemLineDelete(tupleId, pageId, uniqueId, category);
        }}
      >
        <Lines
          estimateId={estimateId}
          estimation={estimation}
          pageId={pageId}
          onDelete={(tupleId: string, uniqueId: string, category?: Category) => onItemLineDelete(tupleId, pageId, uniqueId, category)}
          onSelect={onSelectItemLine}
          lines={linesFiltered ?? []}
          tuples={lines}
          itemCategoryTab={itemCategoryTab}
          onReorder={handleReorder}
        />

        <AddLineItem pageId={pageId} estimateId={estimateId} />
      </RoomProvider>
    </div>
  );
});
